import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, CircularProgress, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Divider, Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { orderListAPI } from '../../services/api';

const PAYMENT_METHOD_COLOR = {
    cash: 'success',
    bank_transfer: 'info',
    cheque: 'warning',
    other: 'default',
};

const PaymentHistoryDialog = ({ open, onClose, order }) => {
    const { t } = useTranslation();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1, limit: 20, total: 0, pages: 1
    });

    const fetchPayments = useCallback(async () => {
        if (!order?._id) return;
        setLoading(true);
        try {
            const res = await orderListAPI.getOrderPayments(order._id, {
                page: pagination.page,
                limit: pagination.limit,
                sortBy: 'paymentDate',
                sortOrder: 'desc'
            });
            setPayments(res.data?.data || []);
            setPagination(prev => ({
                ...prev,
                total: res.data?.pagination?.total ?? 0,
                pages: res.data?.pagination?.pages ?? 1,
            }));
        } catch {
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [order?._id, pagination.page, pagination.limit]);

    useEffect(() => {
        if (open && order) {
            fetchPayments();
        }
    }, [open, order, fetchPayments]);

    if (!order) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {t('orderList.title', 'Payment History')} — {order.orderNumber}
                    </Typography>
                    <Chip
                        label={
                            order.remainingBalance <= 0
                                ? t('orderList.fullyPaid', 'Fully Paid')
                                : t('orderList.outstanding', 'Outstanding')
                        }
                        color={order.remainingBalance <= 0 ? 'success' : 'warning'}
                        size="small"
                    />
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* ── Order Financial Summary ────────────────────────── */}
                <Box
                    sx={{
                        p: 2, mb: 2, borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">
                                {t('orderList.totalPrice')}
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                                {order.totalPrice?.toLocaleString()} {t('common.currency')}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">
                                {t('orderList.paidPrice')}
                            </Typography>
                            <Typography variant="h6" fontWeight={600} color="success.main">
                                {order.paidPrice?.toLocaleString()} {t('common.currency')}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">
                                {t('orderList.remainingBalance')}
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight={600}
                                color={order.remainingBalance > 0 ? 'error.main' : 'success.main'}
                            >
                                {order.remainingBalance?.toLocaleString()} {t('common.currency')}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* ── Payment Records Table ──────────────────────────── */}
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32} />
                    </Box>
                ) : payments.length === 0 ? (
                    <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">
                            {t('orderList.noPayments', 'No payments recorded yet')}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>#</strong></TableCell>
                                    <TableCell>
                                        <strong>{t('orderList.date', 'Date')}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{t('orderList.amount', 'Amount')}</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{t('orderList.branch', 'Branch')}</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{t('orderList.method', 'Method')}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>
                                            {t('orderList.balanceBefore', 'Balance Before')}
                                        </strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>
                                            {t('orderList.balanceAfter', 'Balance After')}
                                        </strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{t('orderList.recordedBy', 'Recorded By')}</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{t('orderList.notes', 'Notes')}</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((payment, index) => (
                                    <TableRow key={payment._id} hover>
                                        <TableCell>
                                            {(pagination.page - 1) * pagination.limit + index + 1}
                                        </TableCell>
                                        <TableCell>
                                            {payment.paymentDate
                                                ? new Date(payment.paymentDate).toLocaleDateString()
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight={600} color="primary.main">
                                                {payment.amount?.toLocaleString()} {t('common.currency')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {payment.branch?.name || '—'}
                                                </Typography>
                                                {payment.branch?.location && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {payment.branch.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t(
                                                    `orderList.payment.methods.${payment.paymentMethod}`,
                                                    payment.paymentMethod?.replace('_', ' ') || 'cash'
                                                )}
                                                color={PAYMENT_METHOD_COLOR[payment.paymentMethod] || 'default'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="text.secondary">
                                                {payment.balanceBefore?.toLocaleString()} {t('common.currency')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                color={payment.balanceAfter > 0 ? 'error.main' : 'success.main'}
                                            >
                                                {payment.balanceAfter?.toLocaleString()} {t('common.currency')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {payment.createdBy?.username || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    maxWidth: 150,
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title={payment.notes || ''}
                                            >
                                                {payment.notes || '—'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentHistoryDialog;